import { useGetApprovingInvoiceReportDisapprovalRoute } from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.Reject.service';
import { useApprovingInvoiceReportsSearchRefetch } from '@/context/services/approving_invoice_reports/refetchContext';
import { useConfirmRejectModal } from '@/features/approving_invoice_reports/components/ConfirmRejectModal/context/ConfirmRejectModalContext';
import { ConfirmRejectForm } from '@/features/approving_invoice_reports/components/ConfirmRejectModal/types';
import { useCheckApprovingReportsProtectedResources } from '@/features/approving_invoice_reports/components/hooks/useCheckApprovingReportsProtectedResources';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

const statusCodeCheckAccessPermission = ['401', '403', '404'];

export const useOptionsReject = (
  reportId: string,
  onCloseDetailDrawer: () => void
) => {
  const { setValue } = useFormContext<ConfirmRejectForm>();
  const { data, error } = useGetApprovingInvoiceReportDisapprovalRoute({
    reportId: reportId,
  });
  const { onCloseConfirmRejectModal } = useConfirmRejectModal();
  const notify = useToastAPI();
  const { t } = useTranslation();
  const { handleCheckPermission } =
    useCheckApprovingReportsProtectedResources();
  const refetchApprovingInvoiceReportsSearch =
    useApprovingInvoiceReportsSearchRefetch();

  const options = useMemo(() => {
    return data?.destinations?.map((item) => ({
      label: item.name || '',
      value: item.wf_step ? String(item.wf_step) : '',
      caption: {
        children: item.office_member_names?.join('、') || '',
        icon: false,
        type: 'helper' as const,
      },
    }));
  }, [data]);

  /** Handle error */

  const showErrorMessages = useCallback(
    (messages?: string) => {
      notify.error({
        title: messages || t('system_error'),
        duration: 5,
      });
    },
    [notify, t]
  );

  const onCheckAccessApprovingReportSuccess = useCallback(() => {
    showErrorMessages(error?.messages?.join(','));
    onCloseDetailDrawer();
  }, [error?.messages, showErrorMessages, onCloseDetailDrawer]);

  const onCheckAccessApprovingReportFailure = useCallback(() => {
    onCloseDetailDrawer();
  }, [onCloseDetailDrawer]);

  useEffect(() => {
    if (!error) return;
    if (statusCodeCheckAccessPermission.includes(error?.code || '')) {
      handleCheckPermission({
        fnCheckPermission: refetchApprovingInvoiceReportsSearch,
        onCheckSuccess: onCheckAccessApprovingReportSuccess,
        onCheckFailure: onCheckAccessApprovingReportFailure,
      });
    } else {
      showErrorMessages(error?.messages?.join(','));
    }
    onCloseConfirmRejectModal();
  }, [
    error,
    onCloseDetailDrawer,
    refetchApprovingInvoiceReportsSearch,
    handleCheckPermission,
    showErrorMessages,
    onCheckAccessApprovingReportSuccess,
    onCloseConfirmRejectModal,
    onCheckAccessApprovingReportFailure,
  ]);

  /** Auto set value for destination field if only one option */
  useEffect(() => {
    if (options?.length === 1) {
      setValue('rejectDestination', options[0]?.value);
    }
  }, [options, setValue]);

  return {
    data,
    options,
  };
};
